<!--
  @fileoverview Unified Base Chart Component
  @module shared-ui/components/BaseChart

  Reusable Chart.js component that works in:
  - Browser (frontend, admin)
  - Node.js SSR (backend PDF generation)

  Supports: line, bar, pie, doughnut, radar, polarArea
-->

<template>
	<div :style="containerStyle">
		<canvas ref="chartCanvas" :aria-label="ariaLabel" />
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onBeforeUnmount, computed } from 'vue';
import type { Chart, ChartConfiguration, ChartType, ChartData, ChartOptions } from 'chart.js';

interface Props {
	/** Chart type */
	type: ChartType;

	/** Chart data */
	data: ChartData;

	/** Chart options (merged with defaults) */
	options?: ChartOptions;

	/** Chart width in pixels */
	width?: number;

	/** Chart height in pixels */
	height?: number;

	/** Accessibility label */
	ariaLabel?: string;

	/** Whether to maintain aspect ratio */
	maintainAspectRatio?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	width: 400,
	height: 300,
	ariaLabel: 'Chart',
	maintainAspectRatio: true,
	options: () => ({}),
});

const emit = defineEmits<{
	/** Emitted when chart is created and ready */
	'chart-ready': [chart: Chart];

	/** Emitted when chart is destroyed */
	'chart-destroyed': [];
}>();

// Template refs
const chartCanvas = ref<HTMLCanvasElement | null>(null);

// Chart instance
let chartInstance: Chart | null = null;

// Container style
const containerStyle = computed(() => ({
	width: `${props.width}px`,
	height: `${props.height}px`,
	maxWidth: '100%',
	position: 'relative' as const,
}));

/**
 * Default chart options
 * Merged with user-provided options
 */
const getDefaultOptions = (): ChartOptions => ({
	responsive: true,
	maintainAspectRatio: props.maintainAspectRatio,
	plugins: {
		legend: {
			display: true,
			position: 'top',
		},
		tooltip: {
			enabled: true,
			backgroundColor: 'rgba(0, 0, 0, 0.8)',
			titleColor: 'white',
			bodyColor: 'white',
			borderColor: 'rgba(255, 255, 255, 0.1)',
			borderWidth: 1,
		},
	},
});

/**
 * Create or update the Chart.js instance
 */
async function renderChart(): Promise<void> {
	if (!chartCanvas.value) return;

	// Destroy existing chart
	if (chartInstance) {
		chartInstance.destroy();
		chartInstance = null;
		emit('chart-destroyed');
	}

	// Dynamically import Chart.js to support both browser and SSR
	const { Chart, registerables } = await import('chart.js');

	// Register all Chart.js components
	Chart.register(...registerables);

	// Merge options
	const mergedOptions: ChartOptions = {
		...getDefaultOptions(),
		...props.options,
	};

	// Chart configuration
	const config: ChartConfiguration = {
		type: props.type,
		data: props.data,
		options: mergedOptions,
	};

	// Create chart instance
	chartInstance = new Chart(chartCanvas.value, config);

	// Emit ready event
	emit('chart-ready', chartInstance);
}

/**
 * Update chart data without recreating
 */
function updateChart(): void {
	if (!chartInstance) return;

	chartInstance.data = props.data;
	chartInstance.options = {
		...getDefaultOptions(),
		...props.options,
	};
	chartInstance.update();
}

// Lifecycle hooks
onMounted(() => {
	renderChart();
});

// Watch for data/options changes
watch(
	[() => props.data, () => props.options],
	() => {
		if (chartInstance) {
			updateChart();
		} else {
			renderChart();
		}
	},
	{ deep: true },
);

// Watch for type changes (requires full re-render)
watch(
	() => props.type,
	() => {
		renderChart();
	},
);

onBeforeUnmount(() => {
	if (chartInstance) {
		chartInstance.destroy();
		chartInstance = null;
		emit('chart-destroyed');
	}
});
</script>
